import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCcw, AlertCircle, CheckCircle, XCircle, Clock, Mail, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

function RefundPolicy() {
  const navigate = useNavigate();

  useEffect(() => {
    // scroll handled by <ScrollRestoration />
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-50">
        <div className="container mx-auto px-6 md:px-12 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-[#00F0FF] hover:text-[#00C2CC]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Marketplace
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-12 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-full bg-white text-black/10 flex items-center justify-center mx-auto mb-4">
              <RefreshCcw className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold mb-4">Refund Policy</h1>
            <p className="text-neutral-400">Last updated: December 2025</p>
          </div>

          {/* Overview Alert */}
          <div className="bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-2xl p-6 mb-8">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-[#00F0FF] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-lg mb-2">Digital Products Policy</h3>
                <p className="text-neutral-300">
                  As a marketplace for digital products, all sales on theailedger.ai are generally 
                  considered final. However, we understand that issues may arise, and we have policies 
                  in place to protect both buyers and sellers.
                </p>
              </div>
            </div>
          </div>

          {/* When Refunds ARE Provided */}
          <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold">When Refunds May Be Issued</h2>
            </div>
            
            <p className="text-neutral-300 mb-4">
              We may issue refunds in the following circumstances:
            </p>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white">Product Not Delivered</strong>
                  <p className="text-neutral-400 text-sm">If you did not receive access to the purchased product within 24 hours</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white">Product Significantly Different</strong>
                  <p className="text-neutral-400 text-sm">If the product is materially different from its description or demo</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white">Technical Issues</strong>
                  <p className="text-neutral-400 text-sm">If the product has critical defects that the seller cannot or will not fix</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white">Duplicate Purchase</strong>
                  <p className="text-neutral-400 text-sm">If you accidentally purchased the same product twice</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white">Unauthorized Transaction</strong>
                  <p className="text-neutral-400 text-sm">If a purchase was made without your authorization</p>
                </div>
              </li>
            </ul>
          </section>

          {/* When Refunds Are NOT Provided */}
          <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-500" />
              </div>
              <h2 className="text-2xl font-bold">When Refunds Are Not Provided</h2>
            </div>
            
            <p className="text-neutral-300 mb-4">
              Refunds will generally not be issued in these cases:
            </p>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white">Change of Mind</strong>
                  <p className="text-neutral-400 text-sm">You simply changed your mind after purchase</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white">Product Already Accessed/Downloaded</strong>
                  <p className="text-neutral-400 text-sm">You have already downloaded or accessed the digital product</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white">Incompatibility Issues</strong>
                  <p className="text-neutral-400 text-sm">The product doesn&apos;t work with your specific setup (check requirements before buying)</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white">Unrealistic Expectations</strong>
                  <p className="text-neutral-400 text-sm">The product doesn&apos;t meet expectations not stated in the description</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-white">Request After 14 Days</strong>
                  <p className="text-neutral-400 text-sm">More than 14 days have passed since the purchase date</p>
                </div>
              </li>
            </ul>
          </section>

          {/* Refund Process */}
          <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#00F0FF]/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <h2 className="text-2xl font-bold">Refund Request Process</h2>
            </div>
            
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#00F0FF] text-black flex items-center justify-center font-bold flex-shrink-0">
                  1
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Contact the Seller First</h4>
                  <p className="text-neutral-400 text-sm">
                    Use the messaging system to contact the seller and explain the issue. 
                    Many issues can be resolved directly with the seller.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#00F0FF] text-black flex items-center justify-center font-bold flex-shrink-0">
                  2
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Submit a Support Request</h4>
                  <p className="text-neutral-400 text-sm">
                    If the seller doesn&apos;t respond within 48 hours or you can&apos;t resolve the issue, 
                    email us at support@theailedger.ai with your order details.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#00F0FF] text-black flex items-center justify-center font-bold flex-shrink-0">
                  3
                </div>
                <div>
                  <h4 className="font-semibold mb-1">We Review Your Request</h4>
                  <p className="text-neutral-400 text-sm">
                    Our team will review your request and may contact both you and the seller. 
                    This typically takes 3-5 business days.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#00F0FF] text-black flex items-center justify-center font-bold flex-shrink-0">
                  4
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Resolution</h4>
                  <p className="text-neutral-400 text-sm">
                    If approved, refunds are processed to your original payment method within 5-10 business days.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Time Limits */}
          <section className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Clock className="w-6 h-6 text-yellow-500" />
              <h2 className="text-2xl font-bold">Time Limits</h2>
            </div>
            <ul className="space-y-2 text-neutral-300">
              <li><strong>14 Days:</strong> Maximum time to request a refund from purchase date</li>
              <li><strong>48 Hours:</strong> Time to wait for seller response before escalating</li>
              <li><strong>3-5 Business Days:</strong> Typical review time for refund requests</li>
              <li><strong>5-10 Business Days:</strong> Time for approved refunds to process</li>
            </ul>
          </section>

          {/* Seller Refund Policies */}
          <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-white text-black/10 flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold">Seller Policies</h2>
            </div>
            <p className="text-neutral-300 mb-4">
              Individual sellers may offer their own refund guarantees that exceed our platform policy. 
              Always check the product description for any additional seller guarantees such as:
            </p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
              <li>30-day money-back guarantees</li>
              <li>Satisfaction guarantees</li>
              <li>Free updates and support</li>
            </ul>
            <p className="text-neutral-400 mt-4 text-sm">
              Seller guarantees are in addition to our platform policy and will be honored.
            </p>
          </section>

          {/* Disputes */}
          <section className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold mb-4">Chargebacks & Disputes</h2>
            <p className="text-neutral-300 mb-4">
              We encourage you to contact us before filing a chargeback with your bank. Chargebacks 
              can result in:
            </p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
              <li>Suspension of your account pending investigation</li>
              <li>Loss of access to purchased products</li>
              <li>Longer resolution times</li>
            </ul>
            <p className="text-neutral-300 mt-4">
              In most cases, we can resolve issues faster through our support process than through 
              a chargeback.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-white text-black/10 border border-white/30 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-6 h-6 text-white" />
              <h2 className="text-2xl font-bold">Need Help?</h2>
            </div>
            <p className="text-neutral-300 mb-4">
              If you have questions about our refund policy or need to request a refund, contact us:
            </p>
            <p className="text-neutral-300">
              <strong>Email:</strong>{' '}
              <a href="mailto:support@theailedger.ai" className="text-white hover:underline">
                support@theailedger.ai
              </a>
            </p>
            <p className="text-neutral-300 mt-2">
              <strong>Response Time:</strong> Within 24-48 hours
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default RefundPolicy;
